import { useMutation, useQuery } from "@tanstack/react-query";

import { toast } from "@/components/ui/use-toast";
import type { ApiError } from "@/lib/api/error";
import { handleApiError } from "@/lib/api/error";
import { defaultNS, useTranslation } from "@/lib/i18n";
import { queryClient } from "@/lib/queryClient";

import {
  archiveActiveCatalog,
  getActiveCatalog,
  getActiveCatalogHeader,
} from "./api";
import { activeCatalogKey } from "./key";

const FIVE_MINUTES_IN_MS = 5 * 60 * 1000;

// 型と定数の再エクスポート
export { activeCatalogKey } from "./key";
export {
  archiveActiveCatalog,
  getActiveCatalog,
  getActiveCatalogHeader,
} from "./api";
export type {
  GetActiveCatalogHeaderResponse,
  GetActiveCatalogResponse,
  DeviceCatalogSection,
  DeviceCatalogAttribute,
  DeviceCatalogAttributeValue,
  ValueType,
} from "./type";

/**
 * Active デバイスカタログ情報のデータをプリフェッチするローダー
 *
 * 各タブページのloaderで使用します。
 * @example
 * ```ts
 * export const Route = createFileRoute(...)({
 *   loader: prefetchActiveCatalog,
 * });
 * ```
 */
export async function prefetchActiveCatalog({
  params,
}: {
  params: { id: string };
}) {
  await queryClient.ensureQueryData({
    queryKey: activeCatalogKey.detail(params.id),
    queryFn: () => getActiveCatalog(params.id),
    staleTime: FIVE_MINUTES_IN_MS,
  });
}

/**
 * Active デバイスカタログ情報取得
 */
export function useGetActiveCatalog(id: string) {
  return useQuery({
    queryKey: activeCatalogKey.detail(id),
    queryFn: () => getActiveCatalog(id),
    staleTime: FIVE_MINUTES_IN_MS,
    refetchOnWindowFocus: false,
  });
}

/**
 * Active デバイスカタログ ヘッダー情報取得
 */
export function useGetActiveCatalogHeader(id: string) {
  return useQuery({
    queryKey: activeCatalogKey.header(id),
    queryFn: () => getActiveCatalogHeader(id),
    staleTime: FIVE_MINUTES_IN_MS,
    refetchOnWindowFocus: false,
  });
}

/**
 * Active デバイスカタログをアーカイブ
 *
 * @param id - カタログID
 * @param options - 成功・失敗時のコールバック
 */
export function useArchiveActiveCatalog(
  id: string,
  options?: {
    onSuccess?: () => void;
    onError?: (error: ApiError) => void;
  },
) {
  const { i18n, t } = useTranslation(defaultNS, {
    keyPrefix: "pages.deviceCatalog.header.archiveDialog",
  });

  return useMutation({
    mutationFn: async (archivedReason?: string | null) => {
      return await archiveActiveCatalog(id, archivedReason);
    },
    onSuccess: () => {
      // アーカイブ後はクエリキャッシュを削除（リフェッチ不要）
      queryClient.removeQueries({
        queryKey: activeCatalogKey.detail(id),
      });
      queryClient.removeQueries({
        queryKey: activeCatalogKey.header(id),
      });

      toast({
        description: t("successMessage"),
      });
      options?.onSuccess?.();
    },
    onError: (error: ApiError) => {
      const { message } = handleApiError(error, {
        language: i18n.language,
      });
      toast({
        description: message,
        variant: "destructive",
      });
      options?.onError?.(error);
    },
  });
}
